import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { Logger } from "./logger";
import { DefaultPerformanceTracker } from "./performance";
import { SentryLogger } from "./sentry";
import type {
  LoggerConfig,
  LoggerContext as LogContext,
  LoggerContextValue,
  ILogger,
  LogLevel,
} from "./types";

const LoggerReactContext = createContext<LoggerContextValue | null>(null);

export interface LoggerProviderProps {
  children: ReactNode;
  config?: LoggerConfig;
  initialContext?: LogContext;
}

export interface LoggerContextValueInternal {
  logger: Logger;
  correlationId: string | null;
  setCorrelationId: (id: string | null) => void;
  generateCorrelationId: () => string;
  withContext: (context: LogContext) => void;
  clearContext: () => void;
}

export const LoggerProvider: React.FC<LoggerProviderProps> = ({
  children,
  config,
  initialContext = {},
}) => {
  // Create logger instance with default config
  const [logger] = useState(() => {
    const defaultConfig: LoggerConfig = {
      // from env  vite
      level: (import.meta.env.VITE_LOG_LEVEL as LogLevel) || "info",
      enableConsole: true,
      enableSentry: true,
      correlationIdGenerator: uuidv4,
      ...config,
    };

    const loggerInstance = new Logger(defaultConfig);

    // Add performance tracker if enabled
    if (defaultConfig.enableSentry || defaultConfig.performanceTracker) {
      const tracker = new DefaultPerformanceTracker(loggerInstance);
      loggerInstance.config.performanceTracker = tracker;
    }

    // Add Sentry integration if enabled
    if (defaultConfig.enableSentry && defaultConfig.sentryConfig) {
      const sentryLogger = new SentryLogger(
        defaultConfig.sentryConfig,
        loggerInstance,
      );
      sentryLogger.init();
    }

    return loggerInstance;
  });

  // State for correlation ID and context
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [context, setContext] = useState<LogContext>(initialContext);

  // Generate correlation ID
  const generateCorrelationId = useCallback(() => {
    const id = logger.generateCorrelationId();
    setCorrelationId(id);
    return id;
  }, [logger]);

  // Set correlation ID
  const handleSetCorrelationId = useCallback(
    (id: string | null) => {
      logger.setCorrelationId(id);
      setCorrelationId(id);
    },
    [logger],
  );

  // Update context
  const updateContext = useCallback((newContext: LogContext) => {
    setContext((prev: LogContext) => ({ ...prev, ...newContext }));
  }, []);

  // Clear context
  const clearContext = useCallback(() => {
    logger.clearContext();
    setContext({});
  }, [logger]);

  // Get logger with context
  const getLoggerWithContext = useCallback(() => {
    return logger.withContext(context);
  }, [logger, context]);

  const value: LoggerContextValue = {
    logger: getLoggerWithContext(),
    correlationId,
    setCorrelationId: handleSetCorrelationId,
    generateCorrelationId,
    withContext: updateContext,
    clearContext,
  };

  return React.createElement(LoggerReactContext.Provider, { value }, children);
};

export const useLoggerContext = (): LoggerContextValue => {
  const context = useContext(LoggerReactContext);

  if (!context) {
    throw new Error(
      "useLoggerContext / useLogger must be used within a LoggerProvider",
    );
  }

  return context;
};

export const useLogger = (): ILogger => {
  const { logger } = useLoggerContext();
  return logger;
};

export const useCorrelationId = (): string | null => {
  const { correlationId } = useLoggerContext();
  return correlationId;
};

export const useLoggerWithOperation = (operation: string): ILogger => {
  const { logger } = useLoggerContext();
  return logger.withContext({ operation });
};
